import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { PdfViewerCanvas } from '../components/pdf/PdfViewerCanvas';
import { PdfViewerToolbar } from '../components/pdf/PdfViewerToolbar';

const DEFAULT_DOCUMENT_TITLE = 'Document';
const DEFAULT_PAGE_NUMBER = 1;
const INITIAL_SCALE = 1.2;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.2;
const HIGHLIGHT_DELAY_MS = 400;
const MIN_SEARCH_TERM_LENGTH = 18;
const MAX_SEARCH_TERMS = 8;
const TRUNCATED_SEARCH_TERM_LENGTH = 120;
const SEARCH_TERM_WORD_LIMIT = 10;
const HIGHLIGHT_BACKGROUND = 'rgba(255, 235, 59, 0.45)';
const HIGHLIGHT_BOX_SHADOW = '0 0 0 1px rgba(255, 235, 59, 0.15)';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

function getRequestedPage(pageValue) {
  const requestedPage = Number(pageValue || DEFAULT_PAGE_NUMBER);
  return Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : DEFAULT_PAGE_NUMBER;
}

function normalizeText(value = '') {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function clearSpanHighlight(span) {
  span.style.backgroundColor = '';
  span.style.borderRadius = '';
  span.style.boxShadow = '';
  span.removeAttribute('data-highlighted');
}

function buildTermCandidates(term) {
  return [term, term.slice(0, TRUNCATED_SEARCH_TERM_LENGTH), term.split(' ').slice(0, SEARCH_TERM_WORD_LIMIT).join(' ')]
    .map((candidate) => normalizeText(candidate))
    .filter((candidate) => candidate.length >= MIN_SEARCH_TERM_LENGTH);
}

function buildSearchTerms(highlightText) {
  const candidates = highlightText
    .split('|')
    .flatMap((segment) => segment.split(/\n+/))
    .flatMap((segment) => [segment, ...segment.split(/[.!?;:]/)])
    .map((segment) => normalizeText(segment))
    .filter((segment) => segment.length >= MIN_SEARCH_TERM_LENGTH);

  return [...new Set(candidates)].slice(0, MAX_SEARCH_TERMS);
}

function findRanges(entries, searchTerms) {
  const fullText = entries.map((entry) => entry.text).join(' ');
  const ranges = [];

  for (const term of searchTerms) {
    const candidates = buildTermCandidates(term);
    for (const candidate of candidates) {
      const start = fullText.indexOf(candidate);
      if (start !== -1) {
        ranges.push([start, start + candidate.length]);
        break;
      }
    }
  }

  return ranges;
}

function buildHighlightEntries(spans) {
  const entries = [];
  let cursor = 0;

  spans.forEach((span) => {
    const text = normalizeText(span.textContent);
    if (!text) {
      return;
    }

    if (entries.length > 0) {
      cursor += 1;
    }

    const start = cursor;
    cursor += text.length;
    entries.push({ span, text, start, end: cursor });
  });

  return entries;
}

function applyHighlightStyles(span) {
  span.style.backgroundColor = HIGHLIGHT_BACKGROUND;
  span.style.borderRadius = '2px';
  span.style.boxShadow = HIGHLIGHT_BOX_SHADOW;
  span.setAttribute('data-highlighted', 'true');
}

function applyHighlights(entries, ranges) {
  let firstMatch = null;
  let count = 0;

  entries.forEach((entry) => {
    const overlaps = ranges.some(([start, end]) => entry.start < end && entry.end > start);
    if (!overlaps) {
      return;
    }

    applyHighlightStyles(entry.span);
    if (!firstMatch) {
      firstMatch = entry.span;
    }
    count += 1;
  });

  return { count, firstMatch };
}

function highlightTextLayer(textLayer, searchTerms) {
  const spans = Array.from(textLayer.querySelectorAll('span')).filter((span) => normalizeText(span.textContent).length > 0);
  spans.forEach(clearSpanHighlight);
  if (searchTerms.length === 0 || spans.length === 0) {
    return { count: 0, firstMatch: null };
  }

  const entries = buildHighlightEntries(spans);
  const ranges = findRanges(entries, searchTerms);
  if (ranges.length === 0) {
    return { count: 0, firstMatch: null };
  }

  return applyHighlights(entries, ranges);
}

function applyHighlightsInContainer(container, highlightText, setHighlightedCount) {
  if (!container) {
    return;
  }

  const searchTerms = buildSearchTerms(highlightText);
  const textLayers = container.querySelectorAll('.react-pdf__Page__textContent');
  let totalHighlights = 0;
  let firstMatch = null;

  textLayers.forEach((textLayer) => {
    const { count, firstMatch: layerFirstMatch } = highlightTextLayer(textLayer, searchTerms);
    totalHighlights += count;
    if (!firstMatch && layerFirstMatch) {
      firstMatch = layerFirstMatch;
    }
  });

  setHighlightedCount(totalHighlights);
  if (firstMatch) {
    firstMatch.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

function clearHighlightTimer(highlightTimerRef) {
  if (highlightTimerRef.current) {
    clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = null;
  }
}

function getHighlightCountMessage(highlightedCount) {
  if (highlightedCount <= 0) {
    return 'Les sections pertinentes sont recherchées sur la page affichée.';
  }

  const pluralSuffix = highlightedCount > 1 ? 's' : '';
  return `${highlightedCount} segment${pluralSuffix} surligné${pluralSuffix} sur la page affichée.`;
}

async function fetchDocumentTitle(documentId) {
  try {
    const response = await fetch(`/api/documents/info/${documentId}`);
    const data = await response.json();
    return data.title || DEFAULT_DOCUMENT_TITLE;
  } catch {
    return DEFAULT_DOCUMENT_TITLE;
  }
}

function usePdfPageState(requestedPage, numPages) {
  const [pageNumber, setPageNumber] = useState(requestedPage);

  useEffect(() => {
    setPageNumber(requestedPage);
  }, [requestedPage]);

  const handlePrevPage = useCallback(() => {
    setPageNumber((current) => Math.max(current - 1, DEFAULT_PAGE_NUMBER));
  }, []);

  const handleNextPage = useCallback(() => {
    setPageNumber((current) => Math.min(current + 1, numPages || DEFAULT_PAGE_NUMBER));
  }, [numPages]);

  return {
    pageNumber,
    handlePrevPage,
    handleNextPage,
  };
}

function usePdfScaleState() {
  const [scale, setScale] = useState(INITIAL_SCALE);

  const handleZoomIn = useCallback(() => {
    setScale((current) => Math.min(current + SCALE_STEP, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((current) => Math.max(current - SCALE_STEP, MIN_SCALE));
  }, []);

  return {
    scale,
    handleZoomIn,
    handleZoomOut,
  };
}

function usePdfDocumentTitle(documentId) {
  const [documentTitle, setDocumentTitle] = useState(DEFAULT_DOCUMENT_TITLE);
  const cancelStateRef = useRef(false);

  const loadDocumentInfo = useCallback(async () => {
    const resolvedTitle = await fetchDocumentTitle(documentId);
    if (!cancelStateRef.current) {
      setDocumentTitle(resolvedTitle);
    }
  }, [documentId]);

  useEffect(() => {
    cancelStateRef.current = false;
    void loadDocumentInfo();
    return () => {
      cancelStateRef.current = true;
    };
  }, [loadDocumentInfo]);

  return documentTitle;
}

function useHighlightManager(containerRef, highlightText, loading, pageNumber, scale) {
  const [highlightedCount, setHighlightedCount] = useState(0);
  const highlightTimerRef = useRef(null);

  const clearScheduledHighlight = useCallback(() => {
    clearHighlightTimer(highlightTimerRef);
  }, []);

  const runHighlightPass = useCallback(() => {
    applyHighlightsInContainer(containerRef.current, highlightText, setHighlightedCount);
  }, [containerRef, highlightText]);

  const scheduleHighlightPass = useCallback(() => {
    clearScheduledHighlight();
    highlightTimerRef.current = window.setTimeout(runHighlightPass, HIGHLIGHT_DELAY_MS);
  }, [clearScheduledHighlight, runHighlightPass]);

  useEffect(() => {
    scheduleHighlightPass();
    return clearScheduledHighlight;
  }, [clearScheduledHighlight, highlightText, loading, pageNumber, scale, scheduleHighlightPass]);

  return {
    highlightedCount,
    scheduleHighlightPass,
  };
}

function PdfHighlightFooter({ highlightText, highlightedCount }) {
  if (!highlightText) {
    return null;
  }

  return (
    <div data-testid="pdf-highlight-indicator" className="border-t border-yellow-800/50 bg-yellow-900/30 px-4 py-2">
      <p data-testid="pdf-highlight-count" className="text-xs text-yellow-400">
        {getHighlightCountMessage(highlightedCount)}
      </p>
    </div>
  );
}

function PdfViewerLayout({ documentTitle, scale, pageNumber, numPages, pdfUrl, handleZoomOut, handleZoomIn, handlePrevPage, handleNextPage, containerRef, loading, error, handleDocumentLoadSuccess, handleDocumentLoadError, handleRenderSuccess, highlightText, highlightedCount }) {
  return (
    <div data-testid="pdf-viewer-page" className="flex min-h-screen flex-col bg-zinc-950 text-white">
      <PdfViewerToolbar
        documentTitle={documentTitle}
        scale={scale}
        pageNumber={pageNumber}
        numPages={numPages}
        pdfUrl={pdfUrl}
        onZoomOut={handleZoomOut}
        onZoomIn={handleZoomIn}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
      />
      <PdfViewerCanvas
        containerRef={containerRef}
        loading={loading}
        error={error}
        pdfUrl={pdfUrl}
        pageNumber={pageNumber}
        scale={scale}
        onDocumentLoadSuccess={handleDocumentLoadSuccess}
        onDocumentLoadError={handleDocumentLoadError}
        onRenderSuccess={handleRenderSuccess}
      />
      <PdfHighlightFooter highlightText={highlightText} highlightedCount={highlightedCount} />
    </div>
  );
}

export default function PdfViewerPage() {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const highlightText = searchParams.get('highlight') || '';
  const requestedPage = getRequestedPage(searchParams.get('page'));
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const pdfUrl = `/api/documents/file/${documentId}`;
  const documentTitle = usePdfDocumentTitle(documentId);
  const { pageNumber, handlePrevPage, handleNextPage } = usePdfPageState(requestedPage, numPages);
  const { scale, handleZoomIn, handleZoomOut } = usePdfScaleState();
  const { highlightedCount, scheduleHighlightPass } = useHighlightManager(containerRef, highlightText, loading, pageNumber, scale);

  const handleDocumentLoadSuccess = useCallback((documentData) => {
    setNumPages(documentData.numPages);
    setLoading(false);
    setError(null);
  }, []);

  const handleDocumentLoadError = useCallback(() => {
    setError('Erreur lors du chargement du PDF');
    setLoading(false);
  }, []);

  return (
    <PdfViewerLayout
      documentTitle={documentTitle}
      scale={scale}
      pageNumber={pageNumber}
      numPages={numPages}
      pdfUrl={pdfUrl}
      handleZoomOut={handleZoomOut}
      handleZoomIn={handleZoomIn}
      handlePrevPage={handlePrevPage}
      handleNextPage={handleNextPage}
      containerRef={containerRef}
      loading={loading}
      error={error}
      handleDocumentLoadSuccess={handleDocumentLoadSuccess}
      handleDocumentLoadError={handleDocumentLoadError}
      handleRenderSuccess={scheduleHighlightPass}
      highlightText={highlightText}
      highlightedCount={highlightedCount}
    />
  );
}
